/**
 * Recursos das Integrações (catálogo, cartões conectados e as telas de AdvBox, Projuris e
 * ControlJUS) que o ponteiro de uma novidade sabe apontar. Ver `spotlightTargets.js`.
 *
 * A tela do hub é a mesma dos Canais: o botão "Nova Integração" carrega a chave do item de menu de
 * quem o abriu (`channels.` em /channels, `integrations.` em /integrations). Os cartões dos
 * números de WhatsApp são dos Canais; aqui ficam os dos sistemas de terceiros.
 *
 * As telas dos sistemas jurídicos têm abas. Todas existem assim que a tela abre, então o destino é
 * a própria tela; só o ControlJUS aceita `?tab=` (as outras duas abrem sempre na Visão Geral).
 * Quem não tem o sistema no plano nem a integração não chega nelas: o ponteiro espera em silêncio.
 */

const HUB = "Catálogo e cartões";
const ADVBOX = "AdvBox";
const PROJURIS = "Projuris ADV";
const CONTROLJUS = "ControlJUS";

const ABRIR_CATALOGO = { key: "integrations.new-integration", text: "Clique em Nova Integração." };

/** Entradas do catálogo "Nova Integração": uma por sistema, todas dentro da janela do catálogo. */
const entrada = (id, name, hint) => ({
  key: `integrations.pick-${id}`,
  navKey: "integrations",
  group: HUB,
  label: `${name} no catálogo`,
  path: "/integrations",
  requires: "panel",
  minLevel: 50,
  hint: `${hint} Aponta a entrada na janela Nova Integração.`,
  steps: [ABRIR_CATALOGO],
});

const abaDoSistema = (provider, feature, group, tabs, { deepLink }) =>
  tabs.map(([tab, label, hint]) => ({
    key: `integrations.${provider}.${tab}`,
    navKey: "integrations",
    group,
    label: `Aba ${label} (${group})`,
    path: `/integrations/${provider}`,
    ...(deepLink ? { query: `tab=${tab}` } : {}),
    requires: "page",
    feature,
    minLevel: 50,
    hint,
  }));

const ABAS_PADRAO = [
  ["overview", "Visão Geral", "Status da conexão, indicadores e o botão de sincronizar. É a aba em que a tela abre."],
  ["crm", "CRM", "Abre o quadro de processos e ajusta o que é sincronizado para o CRM."],
  ["automations", "Automações", "As automações de WhatsApp do sistema: andamentos, tarefas e aniversários."],
  ["history", "Histórico", "Cada execução das automações, com filtro e detalhes."],
  ["notifications", "Notificações", "Quem é avisado quando uma automação falha."],
  ["usage", "Uso & Pacote", "O consumo do mês e o pacote recomendado para a conta."],
];

const ABAS_CONTROLJUS = [
  ["overview", "Visão Geral", "Status da conexão, indicadores e o botão de sincronizar."],
  ["automations", "Automações", "Disparos automáticos a partir dos dados do ControlJUS: andamentos, compromissos e aniversariantes."],
  ["rules", "Regras de fase", "Ações automáticas quando um processo muda de fase."],
  ["history", "Histórico", "Cada execução das automações, com filtro e detalhes."],
  ["notifications", "Notificações", "Quem é avisado quando uma automação falha."],
  ["usage", "Uso & Pacote", "O consumo do mês e o pacote recomendado para a conta."],
];

const novaAutomacao = (provider, feature, group, name, extra) => ({
  key: `integrations.${provider}.new-automation`,
  navKey: "integrations",
  group,
  label: `Botão Nova Automação (${name})`,
  path: `/integrations/${provider}`,
  requires: "panel",
  feature,
  minLevel: 50,
  hint: `Cria uma automação do ${name}, escolhendo o tipo e a mensagem. Fica na aba Automações.`,
  ...extra,
});

export const INTEGRATIONS_TARGETS = [
  // ── Catálogo e cartões ──────────────────────────────────────────────────
  {
    key: "integrations.new-integration",
    navKey: "integrations",
    group: HUB,
    label: "Botão Nova Integração",
    path: "/integrations",
    requires: "page",
    minLevel: 50,
    hint: "Abre o catálogo com tudo que a conta pode conectar: WhatsApp, Google, sistemas jurídicos, Asaas, Claude e mais.",
  },
  {
    key: "integrations.provider-card",
    navKey: "integrations",
    group: HUB,
    label: "Cartão de um sistema conectado",
    path: "/integrations",
    requires: "data",
    minLevel: 50,
    hint: "O cartão de cada sistema conectado que tem tela própria (AdvBox, Projuris, ControlJUS, Trello, SEDEP FAZ, Superlogica, Claude): clicar abre a tela de gestão.",
  },
  entrada("controljus", "ControlJUS", "Conecta a conta ControlJUS com usuário e senha."),
  entrada("advbox", "AdvBox", "Conecta o AdvBox com o token de API."),
  entrada("projuris", "Projuris ADV", "Conecta o Projuris com domínio, usuário e senha."),
  entrada("astrea", "Astrea", "Conecta o Astrea com usuário e senha; usado pelos blocos de fluxo."),
  entrada("faz", "SEDEP FAZ", "Conecta o SEDEP FAZ com o token de API."),
  entrada("pipedrive", "Pipedrive", "Conecta o Pipedrive com o token de API."),
  entrada("trello", "Trello", "Conecta o Trello por uma janela de autorização."),
  entrada("superlogica", "Superlogica", "Conecta o Superlogica para cobrar inadimplentes."),
  entrada("asaas", "Asaas", "Conecta o Asaas com o token de API para gerar cobranças."),
  entrada("zapsign", "ZapSign", "Conecta o ZapSign para gerar contratos para assinatura."),
  entrada("google", "Google Workspace", "Conecta Calendar, Drive, Mail, Meet e Sheets por uma janela de autorização."),
  entrada("anthropic", "Claude (Anthropic)", "Conecta a chave própria da Anthropic para usar os modelos Claude nos agentes."),

  // ── AdvBox ──────────────────────────────────────────────────────────────
  ...abaDoSistema("advbox", "integrations.advbox", ADVBOX, ABAS_PADRAO, { deepLink: false }),
  novaAutomacao("advbox", "integrations.advbox", ADVBOX, "AdvBox", {
    steps: [{ key: "integrations.advbox.automations", text: "Abra a aba Automações." }],
  }),

  // ── Projuris ADV ────────────────────────────────────────────────────────
  ...abaDoSistema("projuris", "integrations.projuris", PROJURIS, ABAS_PADRAO, { deepLink: false }),
  novaAutomacao("projuris", "integrations.projuris", PROJURIS, "Projuris", {
    steps: [{ key: "integrations.projuris.automations", text: "Abra a aba Automações." }],
  }),

  // ── ControlJUS ──────────────────────────────────────────────────────────
  ...abaDoSistema("controljus", "integrations.controljus", CONTROLJUS, ABAS_CONTROLJUS, { deepLink: true }),
  novaAutomacao("controljus", "integrations.controljus", CONTROLJUS, "ControlJUS", {
    query: "tab=automations",
    requires: "page",
  }),
];
